import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, PauseCircle, X, Lock } from 'lucide-react';
import PublicFormRenderer from '@/components/forms/PublicFormRenderer';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PublicFormPage() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isEmbed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/forms/${slug}/schema`);
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Form not found');
        setForm(await res.json());
      } catch (e) { setError(e.message || 'Could not load form'); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  const renderState = () => {
    if (loading) {
      return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
    }
    if (error) {
      return (
        <div className="text-center py-20" data-testid="form-error-state">
          <Lock size={32} className="text-zinc-500 mx-auto mb-3" />
          <p className="text-red-400">{error}</p>
        </div>
      );
    }
    if (!form) return null;

    if (form.status === 'paused') {
      return (
        <div className="text-center py-16" data-testid="form-paused-state">
          <PauseCircle size={48} className="text-yellow-400 mx-auto mb-4" />
          <h2 className="font-syne text-2xl font-bold text-white mb-2">{form.title}</h2>
          <p className="text-zinc-300 max-w-md mx-auto">We're not collecting responses right now. Please check back soon.</p>
        </div>
      );
    }
    if (form.status === 'closed') {
      return (
        <div className="text-center py-16" data-testid="form-closed-state">
          <X size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="font-syne text-2xl font-bold text-white mb-2">{form.title}</h2>
          <p className="text-zinc-300 max-w-md mx-auto">This form has been closed.</p>
        </div>
      );
    }

    return (
      <div className="rounded-2xl bg-[#13153A]/60 backdrop-blur-xl border border-purple-500/20 overflow-hidden">
        {form.cover_image_url && (
          <img
            src={form.cover_image_url.startsWith('http') ? form.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${form.cover_image_url}`}
            alt={form.title}
            className="w-full max-h-64 object-cover"
          />
        )}
        <div className="p-6 sm:p-10">
          <h1 className="font-syne text-3xl sm:text-4xl font-black text-white mb-3 tracking-tight break-words">{form.title}</h1>
          {form.description && <p className="text-zinc-300 mb-6 leading-relaxed whitespace-pre-wrap">{form.description}</p>}
          <PublicFormRenderer form={form} />
        </div>
      </div>
    );
  };

  if (isEmbed) {
    return (
      <div className="min-h-screen bg-[#0B0D2B] p-4">
        <div className="max-w-xl mx-auto">{renderState()}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D2B]">
      <Navbar />
      <main className="container-main pt-28 pb-16" data-testid="public-form-page">
        <div className="max-w-xl mx-auto">{renderState()}</div>
      </main>
      <Footer />
    </div>
  );
}
